import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { OpportunityListPresenter } from './opportunity-list-presenter'
import { opportunityServices } from '../../services'
import { useSelector, useDispatch } from 'react-redux'
import { RootState } from '../../store'
import { Opportunity, SearchParams } from '../../lib/types'
import { showBanner } from '../../global-state/banner-slice'
import { showModel } from '../../global-state/confirm-model-slice'
import { INSUFFICIENT_PERMISSION_MESSAGE } from '../../lib/permissions/uiMessages'
import { EngagementEvents, logCrmEvent } from '../../lib/utils/crmEvents'
import {
  accessMeetsRequired,
  normalizePermissionsFromLogin,
} from '../../lib/permissions/adminFeatures'

const PUBLISH_STATUS_FILTERS = new Set(['PUBLISHED', 'UNPUBLISHED'])

const DEFAULT_SEARCH_PARAMS: SearchParams = {
  limit: '10',
  offset: '0',
  keyword: '',
  filterBy: '',
  status: '',
  sortBy: '',
  closingFilter: 'ACTIVE',
}

function OpportunityListContainer() {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { isConfirm } = useSelector((state: RootState) => state.confirmModel)
  const { permissions, role } = useSelector((state: RootState) => state.gbcUser)
  const effectivePermissions = useMemo(
    () => permissions ?? normalizePermissionsFromLogin(role as string, null),
    [permissions, role],
  )
  const [opportunities, setOpportunities] = useState<Opportunity[]>([])
  const [assetClasses, setAssetClasses] = useState<{ text: string; value: string }[]>([])
  const [searchParams, setSearchParams] = useState<SearchParams>(DEFAULT_SEARCH_PARAMS)
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 10,
    count: 5,
    offset: 0,
  })
  const [currentPage, setCurrentPage] = useState(1)
  const [isDeleted, setIsDeleted] = useState(false)
  const [deleteId, setDeleteId] = useState(0)

  const isCloseStatusToggle = searchParams.closingFilter !== 'CLOSED'

  useEffect(() => {
    const fetchAssetClasses = async () => {
      const data: any = await opportunityServices.getAssetClasses()
      if (data?.status === 200 && data.data && data.data.data) {
        const formattedAssetClasses = data.data.data.map((assetClass: string) => ({
          text: assetClass,
          value: assetClass,
        }))
        setAssetClasses(formattedAssetClasses)
      }
    }
    fetchAssetClasses()
  }, [])

  useEffect(() => {
    const fetchAllProjects = async () => {
      const data: any = await opportunityServices.getSearchOpportunities(searchParams)
      if (data?.status === 200) {
        setOpportunities(data.opportunities)
        setPagination(data.pagination)
      }
    }
    fetchAllProjects()
    setIsDeleted(false)
  }, [currentPage, isDeleted, searchParams])

  useEffect(() => {
    const deleteProject = async () => {
      const data: any = await opportunityServices.deleteProject(deleteId)
      if (data.status === 200) {
        setIsDeleted(true)
        dispatch(
          showBanner({
            text: 'Opportunity deleted successfully',
            variant: 'success-banner',
          }),
        )
      } else {
        dispatch(
          showBanner({
            text: data.data && data.data.message,
            variant: 'error-banner',
          }),
        )
      }
    }
    if (isConfirm && deleteId) deleteProject()
  }, [isConfirm])

  const applySearchParams = (updates: Partial<SearchParams>) => {
    const { limit } = pagination
    setSearchParams({
      ...searchParams,
      ...updates,
      offset: '0',
      limit: limit.toString(),
    })
    setCurrentPage(1)
    setPagination({ ...pagination, offset: 0 })
  }

  const handleSearchParams = async ({ name, value }: { name: string; value: string }) => {
    if (name === 'filter') {
      if (PUBLISH_STATUS_FILTERS.has(value)) {
        applySearchParams({ status: value, filterBy: '' })
      } else {
        applySearchParams({ filterBy: value, status: '' })
      }
      return
    }

    applySearchParams({ [name]: value } as Partial<SearchParams>)
  }

  const handleClosingFilterToggle = () => {
    applySearchParams({
      closingFilter: isCloseStatusToggle ? 'CLOSED' : 'ACTIVE',
    })
  }

  const resetSearchParams = async () => {
    setSearchParams({ ...DEFAULT_SEARCH_PARAMS })
    setCurrentPage(1)
    setPagination({ ...pagination, offset: 0, limit: 10 })
  }

  const handleCurrentPage = (currentPage: number) => {
    const { limit } = pagination
    setCurrentPage(currentPage)
    setSearchParams({
      ...searchParams,
      offset: `${(currentPage - 1) * limit}`,
    })
    setPagination({ ...pagination, offset: (currentPage - 1) * limit })
  }
  const capitalizeStatus = (status: string) => {
    if (status === 'CLOSED') {
      return 'Closed For Investment'
    }
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
  }
  const handleProjectClick = (opportunityTitle: string, projectSlug: string, id: number) => {
    if (!accessMeetsRequired(effectivePermissions.opportunities, 'write')) {
      dispatch(
        showBanner({
          text: INSUFFICIENT_PERMISSION_MESSAGE,
          variant: 'permission-denied-banner',
        }),
      )
      return
    }
    logCrmEvent(EngagementEvents.CLICKED_EDIT_OPPORTUNITY, {
      opportunityId: id,
      opportunityTitle,
    })
    navigate(`/opportunities/${projectSlug}/${id}/edit`)
  }
  const handleProjectDelete = async (id: number) => {
    dispatch(
      showModel({
        name: 'Opportunity Delete',
        message: 'Are you sure? You want to delete opportunity.',
      }),
    )
    setDeleteId(id)
  }

  const handleProjectInvestmentClick = (
    opportunityTitle: string,
    projectSlug: string,
    projectId: number,
  ) => {
    if (!accessMeetsRequired(effectivePermissions.investments, 'read')) {
      dispatch(
        showBanner({
          text: INSUFFICIENT_PERMISSION_MESSAGE,
          variant: 'permission-denied-banner',
        }),
      )
      return
    }
    logCrmEvent(EngagementEvents.CLICKED_VIEW_INVESTMENTS_FOR_OPPORTUNITY, {
      opportunityId: projectId,
      opportunityTitle,
    })

    navigate(`/opportunity/investments/${projectSlug}/${projectId}`)
  }

  const handleProjectWithdrawalClick = (
    opportunityTitle: string,
    projectSlug: string,
    projectId: number,
  ) => {
    if (!accessMeetsRequired(effectivePermissions.withdrawals, 'read')) {
      dispatch(
        showBanner({
          text: INSUFFICIENT_PERMISSION_MESSAGE,
          variant: 'permission-denied-banner',
        }),
      )
      return
    }
    logCrmEvent(EngagementEvents.CLICKED_VIEW_WITHDRAWALS_FOR_OPPORTUNITY, {
      opportunityId: projectId,
      opportunityTitle,
    })

    navigate(`/opportunity/withdrawals/${projectSlug}/${projectId}`)
  }

  const canEditOrder = accessMeetsRequired(effectivePermissions.opportunities, 'write')

  const handleDisplayOrderSave = async (id: number, value: string) => {
    if (!canEditOrder) {
      dispatch(
        showBanner({
          text: INSUFFICIENT_PERMISSION_MESSAGE,
          variant: 'permission-denied-banner',
        }),
      )
      return
    }

    const trimmed = value.trim()
    const displayOrder = trimmed === '' ? null : Number(trimmed)
    if (
      displayOrder !== null &&
      (!Number.isFinite(displayOrder) || !Number.isInteger(displayOrder) || displayOrder < 0)
    ) {
      dispatch(
        showBanner({
          text: 'Order must be a non-negative whole number',
          variant: 'error-banner',
        }),
      )
      return
    }

    const current = opportunities.find((item) => item.id === id)
    const currentOrder = current?.display_order ?? null
    if (currentOrder === displayOrder) {
      return
    }

    const data: any = await opportunityServices.updateOpportunityDisplayOrder(id, displayOrder)
    if (data?.status === 200) {
      setOpportunities((prev) =>
        prev.map((item) => (item.id === id ? { ...item, display_order: displayOrder } : item)),
      )
      dispatch(
        showBanner({
          text: 'Order updated successfully',
          variant: 'success-banner',
        }),
      )
    } else {
      dispatch(
        showBanner({
          text: (data?.data && data.data.message) || 'Failed to update order',
          variant: 'error-banner',
        }),
      )
    }
  }

  return (
    <OpportunityListPresenter
      pagination={pagination}
      handleCurrentPage={handleCurrentPage}
      setCurrentPage={setCurrentPage}
      currentPage={currentPage}
      resetSearchParams={resetSearchParams}
      searchParams={searchParams}
      handleSearchParams={handleSearchParams}
      capitalizeStatus={capitalizeStatus}
      opportunities={opportunities}
      handleProjectClick={handleProjectClick}
      handleProjectDelete={handleProjectDelete}
      handleProjectInvestmentClick={handleProjectInvestmentClick}
      handleProjectWithdrawalClick={handleProjectWithdrawalClick}
      isCloseStatusToggle={isCloseStatusToggle}
      handleClosingFilterToggle={handleClosingFilterToggle}
      assetClasses={assetClasses}
      canEditOrder={canEditOrder}
      handleDisplayOrderSave={handleDisplayOrderSave}
    />
  )
}

export { OpportunityListContainer }
